import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useIntercom } from 'react-use-intercom';

import useAccessBadge from './useAccessBadge';
import { useMetrics } from './useMetrics';
import useTeachingCreditState from './useTeachingCreditState';

export type PaywallType =
  | 'teaching-sessions'
  | 'school-plan-trial'
  | 'premium-feature-gate';

export interface PaywallEmailContext {
  paywallType: PaywallType;
  /** Semantic source label, e.g. 'designer-start'. */
  source?: string;
  /** Legacy `usePaywallPrompt` screen label, e.g. 'schoolPlanPaywall'. */
  screen?: string;
  /** SchoolPlanTrialModal state, e.g. 'hard-block' | 'home' | 'email-gate'. */
  modalState?: string;
}

/**
 * Single source of truth for tracking paywall email submissions across all
 * three paywall modals (WeeklySessionLimit, SchoolPlanTrial,
 * PremiumFeatureGate). Fires one unified event to both Mixpanel and Intercom
 * with the page the user was on plus the user's current teaching-credit and
 * school-plan-trial state.
 */
export function usePaywallEmailTracking() {
  const router = useRouter();
  const metrics = useMetrics();
  const { trackEvent } = useIntercom();
  const { accessBadge } = useAccessBadge();
  const { creditState } = useTeachingCreditState();

  return useCallback(
    (email: string, ctx: PaywallEmailContext) => {
      const props = {
        email,
        page: router.pathname,
        paywallType: ctx.paywallType,
        source: ctx.source,
        screen: ctx.screen,
        modalState: ctx.modalState,
        organizationId: accessBadge?.organization?.id,
        plan: accessBadge?.license?.coarseLevel,
        subPlan: accessBadge?.license?.fineLevel,
        used: creditState?.used,
        remaining: creditState?.remaining,
        weeklyRate: creditState?.weeklyRate,
        bonusRemaining: creditState?.bonusRemaining,
        limit: creditState?.limit,
        hasReachedLimit: creditState?.hasReachedLimit,
        premiumTestPrepPlayed: creditState?.premiumTestPrepPlayed,
        premiumTestPrepRemaining: creditState?.premiumTestPrepRemaining,
        premiumTestPrepLimit: creditState?.premiumTestPrepLimit,
        premiumTestPrepHasReachedLimit: creditState?.premiumTestPrepHasReachedLimit,
      };
      metrics.logEvent('Paywall.EmailSubmitted', props);
      trackEvent('paywall_email_submitted', props);
    },
    [router, metrics, trackEvent, accessBadge, creditState],
  );
}

export default usePaywallEmailTracking;
